'use client';
import type { CommentSubmissionProps } from './data';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import CommentCheckboxGroup from '../CommentCheckboxGroup';
import CommentScore from '../CommentScore';

import {
  Box,
  Button,
  Center,
  Container,
  Divider,
  Link,
  Stack,
  Text,
  Textarea,
  Uploader,
} from '@/components';
import { useInputAttributes, useTranslation, useVoteProduct } from '@/hooks';
import { routes } from '@/statics';
import { standaloneToast, yupValidator } from '@/utils';

const CommentSubmission: CommentSubmissionProps = ({
  canVote,
  productCode,
  productGroupCode,
  voteQuestions,
}) => {
  const { t } = useTranslation('pages.products');
  const [commentScore, setCommentScore] = useState<number | null>(null);
  const {
    mutateAsync: sendComment,
    isPending: sendCommentLoading,
    isSuccess: isSent,
  } = useVoteProduct();

  const status = commentScore
    ? commentScore <= 6
      ? 'angry'
      : commentScore >= 7 && commentScore <= 8
        ? 'poker'
        : 'happy'
    : 'unset';
  const colorByStatus = {
    angry: 'error.500',
    poker: 'yellow.900',
    happy: 'secondary.500',
    unset: 'unset',
  };

  const relatedQuestion =
    commentScore === null
      ? null
      : voteQuestions?.find(
          (item) =>
            item.first_of_range <= commentScore &&
            item.end_of_range >= commentScore
        ) || null;

  const validationSchema = yupValidator.object().shape({
    description: yupValidator
      .string()
      .max(500)
      .label(t('commentDescription'))
      .required(),
    choice: yupValidator.mixed<string[]>().label(t('choice')),
    images: yupValidator
      .mixed<string[]>()
      .label(t('images'))
      .test('test-length-validation', t('noMoreThan4'), (value) => {
        if (value && value?.length > 4) {
          return false;
        }
        return true;
      }),
  });
  const defaultValues = {
    description: '',
    choice: [],
    images: [],
  };

  type FormType = {
    description: string;
    choice?: string[];
    images?: string[];
  };
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver<FormType>(validationSchema),
    defaultValues,
  });

  const onSubmit = async (data: Partial<FormType>) => {
    if (!relatedQuestion || commentScore === null) {
      return standaloneToast({
        status: 'error',
        title: t('pleaseSelectAScore'),
      });
    }
    if (relatedQuestion.type === 'multi_choice' && data.choice?.length === 0) {
      return standaloneToast({
        status: 'error',
        title: t('pleaseSelectAtLeastOneChoice'),
      });
    }

    return await sendComment({
      ...data,
      images: data.images,
      choice: relatedQuestion.type === 'multi_choice' ? data.choice || [] : [],
      order_id: canVote.latest_order_code,
      product_code: productCode,
      product_group_code: productGroupCode,
      question: relatedQuestion.questionOid,
      vote: commentScore,
    });
  };

  const filedInputProps = useInputAttributes<FormType>(
    control,
    register,
    errors,
    {
      description: t('commentDescription'),
      choice: t('choice'),
      images: t('images'),
    }
  );

  if (isSent) {
    return null;
  }

  return (
    <Container>
      <Stack
        borderRadius={{ base: 0, md: 'md' }}
        borderColor="gray.600"
        borderWidth={{ base: 0, md: '1px' }}
        px={{ base: 2, md: '8', lg: '110px', xl: '258px' }}
        pt={{ base: '30px', md: '57px' }}
        pb={{ base: '30px', md: '71px' }}
        mt={{ base: 0, md: '12' }}
        direction="column"
        align="center"
        bg="white"
      >
        <Text fontWeight="500" fontSize={{ base: 'xs', md: 'lg' }} color="text">
          {t('commentOnlyForBuyers')}!
        </Text>
        <Text
          fontWeight="500"
          fontSize={{ base: 'xs', md: 'md' }}
          color="gray.700"
          textAlign="center"
        >
          {t('appreciateForYourComment')}
        </Text>
        <Link href={routes.order.href(canVote.latest_order_code.toString())}>
          <Text
            fontWeight="500"
            fontSize={{ base: 'xs', md: 'md' }}
            color="primary.500"
            borderBottom="1px solid"
            borderColor="primary.500"
            mt="10px"
          >
            {`${t('orderCode')}: ${canVote.latest_order_code} `}
          </Text>
        </Link>
        <CommentScore
          commentScore={commentScore}
          setCommentScore={setCommentScore}
          color={colorByStatus[status]}
          status={status}
        />
        <Stack gap="4" w="full">
          <Center>
            <Text
              fontWeight="500"
              fontSize={{ base: 'xs', md: 'md' }}
              color="gray.700"
              mt="10px"
            >
              {relatedQuestion?.question}
            </Text>
          </Center>
          <Box>
            {relatedQuestion?.choices && (
              <CommentCheckboxGroup
                checkedColor={colorByStatus[status]}
                data={
                  relatedQuestion.choices.map((item) => ({
                    label: item,
                    value: item,
                  })) || []
                }
                {...filedInputProps.choice}
              />
            )}
          </Box>
          <Textarea {...filedInputProps.description} resize="vertical" />
          <Uploader
            variant="filled"
            {...filedInputProps.images}
            multiple={true}
            limit={4}
            accept="image/*"
          />
          <Box display="flex" justifyContent={{ base: 'center', md: 'end' }}>
            <Button
              justifyContent={'center'}
              mt={{ base: '3', md: '26px' }}
              colorScheme="primary"
              type="submit"
              isLoading={sendCommentLoading}
              onClick={(data) => void handleSubmit(onSubmit)(data)}
              size={{ base: 'md', md: 'lg' }}
              width={{ base: 'full', md: 'unset' }}
            >
              {t('send')}
            </Button>
          </Box>
        </Stack>
      </Stack>
      <Divider
        orientation="horizontal"
        color="gray.700"
        display={{ md: 'none' }}
      />
    </Container>
  );
};
export default CommentSubmission;
